/* eslint-disable max-lines */
import React from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import Text from '@this/components/shared/text/text';
import TranslateIgnoreText from '@this/components/shared/text/translate_ignore_text';
import HotelElement from '@this/domain/hotel_element2';
import type Trip from '@this/domain/trip/trip2';
import { digits } from '@this/src/util';
import Box from '@material-ui/core/Box';
import OrderItemContent from '@this/components/trips_management/trips/order_item_content';
import { getColor } from '@this/shared/ui/theme';
import { CalendarLinkButton } from '@this/components/trips_management/trip_detail/calendar/calendar_link_button';
import type OrderItem from '@this/domain/order_item2';
import TransportElement from '@this/domain/transport_element2';
import OrderItemStatusLabelTemplate from './order_item_status_label.template';
import CalendarAddButton from './calendar/calendar_add_button';
import type { Theme } from './trip_calendar_helpers';

interface Props {
  trip: Trip;
  user: any;
  theme: Theme;
  serviceId: number;
  simpleRequestType: boolean;
  showItemCancelModal?: (orderItem: OrderItem) => void;
  hideFooter?: boolean;
  registeredCalendarEvents?: { [key: string]: any };
}

const TripCardLeft: React.FC<Props> = ({
  trip,
  user,
  theme,
  serviceId,
  simpleRequestType,
  showItemCancelModal,
  hideFooter,
  registeredCalendarEvents
}) => {
  const travelerNames: string[] = trip.travelerNames;

  return trip.draft ? (
    <BlockContent>
      <Box width="auto" margin="0 0 20px" border="none" style={{ verticalAlign: 'top' }}>
        <tbody>
          {trip.title ? (
            <tr>
              <MainTdData>旅程名</MainTdData>
              <MainTd>
                <TranslateIgnoreText text={trip.title} />
              </MainTd>
            </tr>
          ) : (
            <tr />
          )}
          {trip.inAdvanceApprovals ? (
            <tr>
              <MainTdData>申請者</MainTdData>
              <MainTd>
                <TranslateIgnoreText text={trip.user?.name} />
              </MainTd>
            </tr>
          ) : (
            <tr />
          )}
          {travelerNames &&
            travelerNames.map((tn, i) => (
              <tr key={i}>
                {travelerNames.length > 1 ? (
                  <MainTdData>{`出張者${i + 1}`}</MainTdData>
                ) : (
                  <MainTdData>出張者</MainTdData>
                )}
                <MainTd>
                  <TranslateIgnoreText text={tn} />
                </MainTd>
              </tr>
            ))}
          <tr>
            <MainTdData>期間</MainTdData>
            <MainTd>
              <TranslateIgnoreText text={trip.dateRange} />
            </MainTd>
          </tr>
          {trip.finalDestination ? (
            <tr>
              <MainTdData>訪問先</MainTdData>
              <MainTd>
                <TranslateIgnoreText text={trip.finalDestination} />
              </MainTd>
            </tr>
          ) : (
            <tr />
          )}
          {trip.purpose ? (
            <tr>
              <MainTdData>目的</MainTdData>
              <MainTd>
                <TranslateIgnoreText text={trip.purpose} />
              </MainTd>
            </tr>
          ) : (
            <tr />
          )}
        </tbody>
      </Box>
    </BlockContent>
  ) : (
    <>
      {trip.order?.orderItems.map((orderItem, i: number) => (
        <MainContent key={`${i}`} isCanceled={orderItem.isCanceled}>
          <MainContentBody>
            {!trip.draft &&
              (trip.currentApprovalStatus === undefined || ![0, 2].includes(trip.currentApprovalStatus)) &&
              // trip.statusが0のとき、0が表示されるためboolean化
              !!trip.status &&
              ![0, 8, 9, 10, 11, 12].includes(trip.status) &&
              !trip.isApproveCanceled && (
                <HeadStatus>
                  <OrderItemStatusLabelTemplate orderItem={orderItem} />
                </HeadStatus>
              )}
            {orderItem.orderItemCategory === 'arrangement_request' ? (
              <MainBlock>
                <BlockTitle>リクエストフォーム利用料</BlockTitle>
                <BlockContent>
                  <span data-wovn-ignore>{digits(orderItem.price?.totalPriceWithMargin)}</span>
                  <span>円</span>
                </BlockContent>
              </MainBlock>
            ) : (
              <OrderItemContent
                orderItem={orderItem}
                trip={trip}
                user={user}
                theme={theme}
                serviceId={serviceId}
              />
            )}
          </MainContentBody>
          {!hideFooter && (
            <MainContentFooter>
              {(trip.isApproved || _.isEmpty(trip.inAdvanceApprovals)) &&
                showItemCancelModal &&
                orderItem.hasNotEnded && (
                  <CancelRequestButton
                    onClick={() => showItemCancelModal(orderItem)}
                    disabled={orderItem.isCanceled}
                  >
                    <CancelRequestButtonText>個別キャンセル依頼</CancelRequestButtonText>
                  </CancelRequestButton>
                )}
              <CalendarAddButton
                provider="google"
                trip={trip}
                user={user}
                theme={theme}
                orderItem={orderItem}
                registeredCalendarEvents={registeredCalendarEvents}
              />
              <CalendarAddButton
                provider="outlook"
                trip={trip}
                user={user}
                theme={theme}
                orderItem={orderItem}
                registeredCalendarEvents={registeredCalendarEvents}
              />
            </MainContentFooter>
          )}
        </MainContent>
      ))}
      {!_.isEmpty(_.get(trip, 'nonOrderItems')) && (
        <MainContent>
          <MainContentBody>
            <BlockTextL>経費予定分</BlockTextL>
            {trip.nonOrderItems.map((item, idxNonOrder: number) => (
              <MainBlock key={idxNonOrder}>
                <BlockTitle>経路{idxNonOrder + 1}</BlockTitle>
                {item.elements.map((element, j: number) => (
                  <BlockFlex key={j}>
                    <BlockFlexLabel>
                      【
                      {element instanceof TransportElement && element.transportType !== 'other'
                        ? element.title
                        : '徒歩'}
                      】
                    </BlockFlexLabel>
                    <BlockContent>
                      <BlockTextM>
                        <Text text={element.summary} />
                      </BlockTextM>
                      {typeof element.detail === 'function' && (
                        <BlockTextM>
                          {element instanceof HotelElement ? (
                            <>
                              <Text data-wovn-ignore text={element.hotelTypeDetail} />
                              {element.detailPath && (
                                <div data-wovn-ignore>
                                  <span>宿泊詳細情報：</span>
                                  <span>
                                    <a href={element.detailPath} target="_blank" rel="noreferrer noopener">
                                      詳細はコチラ
                                    </a>
                                  </span>
                                </div>
                              )}
                              <p data-wovn-ignore>
                                住所:{' '}
                                <a href={element.hotelMapAddressLink} target="_blank" rel="noopener noreferrer">
                                  {element.address ? element.address : '所在地を確認'}
                                </a>
                              </p>
                              <p data-wovn-ignore>{element.telDescription}</p>
                              <Text data-wovn-ignore text={element.roomTypeDetail} />
                            </>
                          ) : (
                            <Text data-wovn-ignore text={element.detail()} />
                          )}
                          {simpleRequestType ? (
                            <Text
                              data-wovn-ignore
                              text={`【${item.tripTypeText}】${digits(item.price.totalPrice)}円`}
                            />
                          ) : (
                            <Text data-wovn-ignore text={`${digits(item.price.totalPrice)}円`} />
                          )}
                        </BlockTextM>
                      )}
                    </BlockContent>
                  </BlockFlex>
                ))}
                {!simpleRequestType && (
                  <>
                    <BlockTextM>
                      <Text text="※表記は現地時刻です" />
                    </BlockTextM>
                    <BlockTextM>
                      <Text data-wovn-ignore text={`${digits(item.price.totalPrice)}円`} />
                    </BlockTextM>
                  </>
                )}
              </MainBlock>
            ))}
          </MainContentBody>
        </MainContent>
      )}
    </>
  );
};

const BlockContent = styled.div`
  line-height: 1.8;
`;

const MainTd = styled.td`
  padding: 5px 0;
  border: none;
  vertical-align: top;
  color: ${props => props.theme.grayTextColor}
  font-weight: bold;

  ${media.sp`
    padding: 3px 0;
  `}
`;

const MainTdData = styled(MainTd)`
  width: 60px;
  color: ${props => props.theme.disabledColor};
`;

const MainBlock = styled.div``;

interface MainContentProps {
  isCanceled?: boolean;
}

const MainContent = styled.div<MainContentProps>`
  border: solid 1px ${props => props.theme.grayBorderColor};
  background-color: ${props => (props.isCanceled ? getColor('background', 'disabled') : '#ffffff')};
  margin-bottom: 12px;
  position: relative;
`;

const MainContentBody = styled.div`
  padding: 12px;
`;

const MainContentFooter = styled.div`
  padding: 12px;
  border-top: solid 1px ${props => props.theme.grayBorderColor};
  text-align: center;
  display: flex;
  justify-content: flex-end;
  gap: 8px;

  ${media.sp`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;

    & > button {
      flex: 1 1 auto;
      min-width: 120px;
      margin-bottom: 8px;
    }
  `}
`;

const BlockFlex = styled.div`
  display: flex;
  gap: 8px;
`;

const BlockFlexLabel = styled.div`
  min-width: 70px;
`;

const BlockTitle = styled.p`
  font-size: 14px;
  font-weight: bold;
  line-height: 1.6;
  margin-bottom: 10px;

  ${media.sp`
    font-size: 12px;
  `}
`;

const BlockTextL = styled.div`
  font-size: 14px;
  font-weight: bold;
  ${media.sp`
    font-size: 12px;
  `}
`;

const BlockTextM = styled.div``;

const HeadStatus = styled.div`
  padding: 8px 14px;
  background: ${props => props.theme.grayBgColor};
  border-radius: 14px;
  font-size: 12px;
  line-height: 1;
  font-weight: bold;
  text-align: center;
  width: 130px;
  position: absolute;
  right: 12px;
  top: 12px;
`;

const CancelRequestButton = styled(CalendarLinkButton)``;

const CancelRequestButtonText = styled.span`
  font-size: 11px;
  letter-spacing: 0;
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  ${media.sp`
    font-size: 10px;
  `}
`;

export default TripCardLeft;
